import {unwrapResult} from '@reduxjs/toolkit';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {requestOK} from 'redux-thunk-kit';
import {
  Col,
  UiBox,
  UiButton,
  UiFormControl,
  UiFormErrorMessage,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
  UiTextarea,
} from '~/components/elements';
import {PAGE_URLS} from '~/constants';
import {WEIGHT_LOSS_TWO_KEYS, WEIGHT_LOSS_TWO_QUESTIONNAIRES, YES_NO_VALUES} from '~/constants/medical-questionnaires';
import {useInput, useNotification, useRequestState} from '~/hooks';
import {setMedicalUUID, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {getMdqStatus, submitOrLogMdq} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';

interface Props {}

const MDQ2Questionnaire9Screen = ({}: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const mdq = useSelector((state: RootState) => state.questionnaires);
  const {addMessage} = useNotification(dispatch);
  const {methodKey, medicalUUID, answerOrder, collectedData} = mdq;
  const {loading} = useRequestState(submitOrLogMdq);
  const {loading: checkStatusLoading} = useRequestState(getMdqStatus);
  //   const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  //   const {wpAuthUser} = medicalQuestionnaire;

  //   const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = WEIGHT_LOSS_TWO_QUESTIONNAIRES.find(i => i.key === WEIGHT_LOSS_TWO_KEYS.MEDICAL_HISTORY);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectedData[WEIGHT_LOSS_TWO_KEYS.MEDICAL_HISTORY]?.data,
  );

  const orderData = collectedData[WEIGHT_LOSS_TWO_KEYS.START]?.data;

  const rules = {
    medicalHistory: (attributes: any) => {
      if (isYesSelected) {
        return {
          presence: {allowEmpty: false, message: `^This field can't be blank`},
          length: {
            minimum: 5,
            tooShort: '^This field is too short (minimum is %{count} characters)',
            tokenizer: function (value: any) {
              return trimSpaces(value);
            },
          },
        };
      }
      return undefined;
    },
  };

  const {inputs, setInputs, trimSpaces, validation} = useInput(
    {
      selectedOption: currentAnswer?.selectedOption || '',
      medicalHistory: currentAnswer?.medicalHistory || '',
    },
    {rules},
  );

  const isYesSelected = inputs.selectedOption === YES_NO_VALUES.YES;

  const onClickSubmit = async () => {
    if (isYesSelected) {
      const errors = validation.getErrors();
      if (errors) {
        return;
      }
    }
    const answerData = {
      key: WEIGHT_LOSS_TWO_KEYS.MEDICAL_HISTORY,
      index: questionnaire?.index || '',
      data: {
        selectedOption: inputs?.selectedOption,
        medicalHistory: isYesSelected ? inputs?.medicalHistory : '',
      },
      order: answerOrder + 1,
    };

    const statusCheckRes = await dispatch(getMdqStatus({id: medicalUUID || ''}));

    if (requestOK(statusCheckRes, getMdqStatus)) {
      const statusResult = unwrapResult(statusCheckRes);
      if (statusResult?.status === 'submitted' || statusResult?.status === 'expired') {
        addMessage('Medical Questionnaire already submitted or expired', 'error');
        return;
      }
    }

    dispatch(setQuestionnaireAnswer(answerData));
    const res = await dispatch(
      submitOrLogMdq({
        data: {
          uid: medicalUUID,
          methodKey: 'mdq2',
          status: 'submitted',
          data: {
            ...collectedData,
            [WEIGHT_LOSS_TWO_KEYS.MEDICAL_HISTORY]: answerData,
          },
        },
      }),
    );

    if (requestOK(res, submitOrLogMdq)) {
      history.push(PAGE_URLS.MDQ2_THANK_YOU + window.location.search);
    }

    if (res.payload?.errMsg === 'Medical Form invalid') {
      dispatch(setMedicalUUID({uid: null}));
      history.replace(PAGE_URLS.MDQ2_HOME + window.location.search);
    }
  };

  const renderMedicalHistory = () => {
    if (!isYesSelected) return null;
    return (
      <UiBox mt={6}>
        <UiText fontWeight="600" mb={2.5}>
          {questionnaire?.questions?.sub}
        </UiText>
        <UiFormControl id="medicalHistory" isRequired isInvalid={!!validation?.errors?.medicalHistory}>
          <UiTextarea
            bg="white"
            value={inputs.medicalHistory}
            onChange={({target}) => setInputs({medicalHistory: target.value})}
            minH="300px"
            borderRadius={10}
            placeholder="Provide as much detail as possible"
            py={4}
            px={4}
          />
          {validation?.errors?.medicalHistory && (
            <UiFormErrorMessage>{validation?.errors?.medicalHistory[0]}</UiFormErrorMessage>
          )}
        </UiFormControl>
      </UiBox>
    );
  };

  const renderQuestions = () => {
    if (!isYesSelected)
      return (
        <UiBox w="100%" mt={9}>
          <UiText fontSize="sm" fontWeight="400" color="liteOuter">
            {questionnaire?.descriptions?.default}
          </UiText>
        </UiBox>
      );
    return (
      <UiBox w="100%" className="animate__animated animate__fadeInLeft">
        {renderMedicalHistory()}
      </UiBox>
    );
  };

  return (
    <UiQuestionnaireLayout
      backIcon
      actions={questionnaire?.actions}
      handleOnNext={onClickSubmit}
      checkoutStep={questionnaire?.checkoutStep}
      pageTitle={questionnaire?.title}
      disableNext={!inputs.selectedOption}
      loading={loading || checkStatusLoading}
    >
      <Col textAlign="center" mt={20} px={2}>
        <UiText fontSize="xl" fontWeight="bold" mb={6}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiSimpleGrid columns={2} spacing={4}>
          {questionnaire?.options?.map(value => (
            <UiButton
              key={`${value.label}-${value.value}`}
              title={value.label}
              variant={inputs.selectedOption === value.value ? 'primary' : 'outline'}
              onClick={() => setInputs({selectedOption: value.value})}
              whiteSpace="pre-wrap"
              color="trueBlack"
              borderColor="trueBlack"
              fontWeight={600}
              borderRadius={48}
              _hover={{}}
            />
          ))}
        </UiSimpleGrid>
        {renderQuestions()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default MDQ2Questionnaire9Screen;
